// --------------------------------------------------------------------------
// Body of EOx page
// --------------------------------------------------------------------------

import React, { useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import axiosAuthInstance from '../../context/security';
import { Box, Flex, Spacer, Image, Heading, Text, Input, Textarea,
  Tabs, Tab, TabList, TabPanels, TabPanel,
  Button, NumberInput,
  NumberInputField, InputGroup, InputRightAddon, FormControl, FormLabel, Divider,
  Grid, Spinner, Card, CardBody, CardHeader, Checkbox, Table, Thead, Tbody, Tr, Th,
  Td, Badge, HStack, VStack, Tooltip, useTheme, useToast, Stat, StatLabel, StatNumber,
  StatHelpText, Switch } from "@chakra-ui/react";
import { ChevronRight, ChevronLeft, Circle } from "@mui/icons-material";
import { useEOxContext } from '../../context/eox_context';
import MapEOx from './MapEOx';
import "../styles.css";


const BodyEOx: React.FC = () => {

  // 1. State, hooks, event handling and helper functions Body
  const theme = useTheme();
  const toast = useToast();
  const [tabIndex, setTabIndex] = useState(0);
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const sidebarWidth = "300px";
  const {geojsonDataInput, errorInputGeoJSON,
    handleGeoJSONChange, startDateInput,
    handleStartDateChange, endDateInput, handleEndDateChange, 
    cloudsInput, handleCloudsChange, selectedDatasets, catalogData,
    fullAoiInput, handleFullAoiChange,
    searchDatasets, handleDatasetsSearchChange, filteredDatasets,
    handleSelectFilteredDatasets, handleDatasetsChange, dropdownVisible,
    searchRef, errorDataset, mapRef } = useEOxContext();
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [responseSuccess, setResponseSuccess] = useState(false);
  const [responseData, setResponseData] = useState<ResponseData>({
      	 area_of_interest: {}, time_range: "", data: {}, n_data: 0});
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [errorRequest, setErrorRequest] = useState('');

  // Handle toggle of sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  // Handle submit to backend EOx endpoint
  const handleSubmit = async () => {
    setIsSubmittingRequest(true);
    setErrorRequest('');
    setResponseSuccess(false);
    setTabIndex(1);

    // Filter only selected datasets
    interface DatasetsInput {
      [hostKey: string]: string[];
    }

    const datasetsInput = Object.entries(selectedDatasets)
    .filter(([_, isSelected]) => isSelected)  
    .reduce<DatasetsInput>((acc, [combinedKey]) => {
        const [datasetKey, hostKey] = combinedKey.split('-:-');
        if (!acc[hostKey]) {
            acc[hostKey] = [];
        }
        acc[hostKey].push(datasetKey);
        return acc; 
    }, {} as DatasetsInput); 

    const requestData = {
      area_of_interest: geojsonDataInput,
      start_date: startDateInput,
      end_date: endDateInput,
      data_catalog: datasetsInput,
      cloud_coverage: cloudsInput,
      full_aoi: fullAoiInput
    }

    try {
      const response = await axiosAuthInstance.post('/eox/find', requestData);
      
      // Handle the response data
      setResponseData(response.data);
      setResponseSuccess(true);

    } catch (error) {
      // Handle errors
      let errorMessage = 'An unexpected error occurred';
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ErrorResponse>;
        errorMessage = axiosError.message;
        if (axiosError.response && axiosError.response.statusText){
          errorMessage += `: ${axiosError.response.statusText}`;
        }
        if (axiosError.response && axiosError.response.data &&
            axiosError.response.data.detail &&
            axiosError.response.data.detail.length > 0) {
          errorMessage += ` - ${axiosError.response.data.detail}`;
        }  
      }
      setErrorRequest(errorMessage);
      toast({
        title: "API Error.",
        description: errorMessage,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  // Handle error response
  interface ErrorDetail {
    msg: string;
  }
  interface ErrorResponse {
    message: string;
    detail?: ErrorDetail[];
  }

  // Handle display of results
  const handleResultDisplay = async (tilejsonUrl: string, resultId: string, resultIndex: number) => {
    try {
      const response = await fetch(tilejsonUrl);
      const tilejsonObj = await response.json();
  
      // Check if mapRef.current is available
      if (mapRef.current && mapRef.current.isStyleLoaded()) {

        // Remove existing tile layer if necessary
        if (mapRef.current.getLayer('tile-layer')) {
          mapRef.current.removeLayer('tile-layer');
          mapRef.current.removeSource('tile-source');
        }
  
        // Add the tile layer from the TileJSON object
        mapRef.current.addSource('tile-source', {
          type: 'raster',
          tiles: tilejsonObj.tiles,
          tileSize: tilejsonObj.tile_size || 256,
        });
  
        mapRef.current.addLayer({
          id: 'tile-layer',
          type: 'raster',
          source: 'tile-source',
          minzoom: tilejsonObj.minzoom,
          maxzoom: tilejsonObj.maxzoom,
          layout: {},
        });
  
        setSelectedResultId(resultId);
        setSelectedIndex(resultIndex);
      }
    } catch (error) {
      toast({
        title: "Data Error.",
        description: 'Error fetching TileJSON from data provider.',
        status: "error",
        duration: 9000,
        isClosable: true,
      });      
    }
  };

  // Clean-up when switching to parameter/input tab
  useEffect(() => {
    if (tabIndex !== 1 && mapRef.current && mapRef.current.isStyleLoaded()) {
      if (mapRef.current.getLayer('tile-layer')) {
        mapRef.current.removeLayer('tile-layer');
      }
      if (mapRef.current.getSource('tile-source')) {
        mapRef.current.removeSource('tile-source');
      }
      setSelectedResultId(null);
      setSelectedIndex(null);
    }
  }, [tabIndex, mapRef]);


  // Response data
  interface ResponseData {
    area_of_interest: {};
    time_range: string;
    data: { [hostKey: string]: ResultItemCollection }; 
    n_data: number;
  }

  interface ResultItemCollection {
    type: string;
    features?: ResultItem[];
  }

  interface ResultItem {
    id: string;
    type: string;
    geometry: {
      type: string;
      coordinates: any;
    };
    bbox: number[];
    properties: { [propertyKey: string]: any };
    stac_version?: string;
    links?: Array<any>;
    assets?: { [assetKey: string]: any }; 
    stac_extensions?: Array<any>;
    collection?: string;
  }

  // Format the date to a string without the weekday
  const formatUTC = (utcDateString: string) => {
    const date = new Date(utcDateString);
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'UTC',
    };
    const formattedParts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
    const year = formattedParts.find(part => part.type === 'year')?.value;
    const month = formattedParts.find(part => part.type === 'month')?.value;
    const day = formattedParts.find(part => part.type === 'day')?.value;
    const hour = formattedParts.find(part => part.type === 'hour')?.value;
    const minute = formattedParts.find(part => part.type === 'minute')?.value;
    const second = formattedParts.find(part => part.type === 'second')?.value;
    const formattedString = `${month}/${day}/${year} ${hour}:${minute}:${second} UTC`;
    
    return formattedString;
  };

  // Format datetime with short name for month and without seconds
  const formatUTCAlternative = (utcDateString: string) => {
    const date = new Date(utcDateString);
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC',
    };

    const formattedParts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
    const year = formattedParts.find(part => part.type === 'year')?.value;
    const month = formattedParts.find(part => part.type === 'month')?.value;
    const day = formattedParts.find(part => part.type === 'day')?.value;
    const hour = formattedParts.find(part => part.type === 'hour')?.value;
    const minute = formattedParts.find(part => part.type === 'minute')?.value;
    
    const formattedString = `${month} ${day}, ${year}, ${hour}:${minute} UTC`;
    
    return formattedString;
  };

  // Format datetime with short name for month and without time
  const formatUTCShort = (utcDateString: string) => {
    const date = new Date(utcDateString);
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
    };
    const formattedParts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
    const year = formattedParts.find(part => part.type === 'year')?.value;
    const month = formattedParts.find(part => part.type === 'month')?.value;
    const day = formattedParts.find(part => part.type === 'day')?.value; 
    const formattedString = `${month} ${day}, ${year}`;
    
    return formattedString;
  };
  
  // Placeholder Image
  const placeholderImage = 'https://via.placeholder.com/50';

  // Mapping of Host Keys and Abbrevations
  const hostKeyAbbrevations: { [key: string]: string } = {
    "planetary_computer": "PC",
  }

  // Effect to show toast when errorDataset is not empty
  useEffect(() => {
    if (errorDataset) {
      toast({
        title: "Dataset Error.",
        description: errorDataset,
        status: "error",
        duration: 20000,
        isClosable: true,
      });
    }
  }, [errorDataset, toast]);

  // Effect to show toast when errorInputGeoJSON is not empty
  useEffect(() => {
    if (errorInputGeoJSON) {
      toast({
        title: "Incorrect GeoJSON.",
        description: errorInputGeoJSON,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [errorInputGeoJSON, toast]);

  // 2. JSX Body
  return (
    <Flex flex="1" position="relative" overflow="hidden">
      <MapEOx />

      <Button
        onClick={toggleSidebar}
        position="absolute"
        left={isSidebarOpen ? sidebarWidth : "0"} 
        top="50%" transform="translateY(-50%)"
        zIndex="docked"
        pointerEvents="auto"
        borderLeftRadius={0} px={0} shadow="lg">
        {isSidebarOpen ? <ChevronLeft /> : <ChevronRight />}
      </Button>

      <Grid
        position="absolute"
        top="0" left="0"
        h="100%"
        w="100%"
        templateColumns={isSidebarOpen ? `${sidebarWidth} 1fr` : `0 1fr`}
        overflowX="hidden" pointerEvents="none" zIndex="docked">
        <Box
          id="sidebar"
          py={4} px={isSidebarOpen ? 1 : 0}
          width={isSidebarOpen ? sidebarWidth : "0"}
          maxHeight="100%"
          bg={theme.colors.main.box}
          color="white"
          overflowY={isSidebarOpen ? "auto" : "hidden"}
          zIndex="popover"
          transition="width 0.1s ease, padding 0.1s ease"
          pointerEvents="auto">
          <Tabs
            variant='soft-rounded'
            index={tabIndex} onChange={(index) => setTabIndex(index)}>
            <TabList>
              <Tab
                sx={{_selected: {
                  color: theme.colors.main.highlightDark,
                  bg: theme.colors.main.highlightLight,
                  },
                  color: "white", bg: "transparent", fontFamily: "label",
                  fontWeight:"400"}}>
                Parameters
              </Tab>
              <Tab
                sx={{_selected: {
                  color: theme.colors.main.highlightDark,
                  bg: theme.colors.main.highlightLight,
                  },
                  color: "white", bg: "transparent", fontFamily: "label",
                  fontWeight:"400"}}>
                Results
              </Tab>
            </TabList>
            <TabPanels>

              {/* Input or Parameter Tab */}
              <TabPanel>
                <FormControl isRequired>              
                  <FormLabel
                    mb={2}
                    fontSize={theme.fontSize.cardName}>
                    01 Area of Interest
                  </FormLabel>
                  <Text
                    mb={2} fontSize={theme.fontSize.info}>
                    Define your area of interest as a GeoJSON Feature
                    or draw your area directly on the map.
                    Note that you can only draw one feature.
                  </Text>
                  <Textarea
                    placeholder='{"type": "Feature","geometry": {}}'
                    height="200px"
                    className="discreet-scrollbar"
                    value={geojsonDataInput ? JSON.stringify(geojsonDataInput, null, 2) : ''}
                    onChange={handleGeoJSONChange}
                    isRequired={true}/>
                </FormControl>
                <Divider mt={6} mb={4}/>
                <FormControl isRequired>
                  <FormLabel
                    mb={2}
                    fontSize={theme.fontSize.cardName}>
                    02 Period of Time
                  </FormLabel>
                  <Text
                    fontFamily="label" fontSize={theme.fontSize.body}>
                    Start Date
                  </Text>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Select the date for the beginning of the archive  
                    search from the calender.
                  </Text>
                  <Input mb={4} 
                    type="datetime-local" isRequired={true}
                    value={startDateInput}
                    onChange={handleStartDateChange}
                    sx={{
                      '::-webkit-calendar-picker-indicator': {
                        filter: 'invert(1)',
                      },
                    }}/>
                  <Text
                    fontFamily="label" fontSize={theme.fontSize.body}>
                    End Date
                  </Text>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Select the date for the end of the archive
                    search from the calender.
                  </Text>
                  <Input
                    type="datetime-local" isRequired={true}
                    value={endDateInput}
                    onChange={handleEndDateChange}
                    sx={{
                      '::-webkit-calendar-picker-indicator': {
                        filter: 'invert(1)',
                      },
                    }}/>
                </FormControl>
                <Divider mt={6} mb={4}/>
                <FormControl isRequired>
                  <FormLabel
                    mb={2}
                    fontSize={theme.fontSize.cardName}>
                    03 Datasets
                  </FormLabel>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Select all Earth Observation datasets
                    that shall be considered.
                  </Text>
                  <Box position="relative" mb={2} ref={searchRef}>
                    <Input
                      placeholder="Search datasets..." value={searchDatasets}
                      onChange={handleDatasetsSearchChange} mb={2}/>
                    {searchDatasets && dropdownVisible && (
                      <Box
                        color="white"
                        position="absolute"
                        p={2} mt={1}
                        borderRadius="sm" shadow="dark-lg"
                        width="full" zIndex="popover"
                        bg="black" className="discreet-scrollbar"
                        maxH="200px" overflowY="auto">
                        <Checkbox
                          isChecked={filteredDatasets.every((datasetKey) => {
                            const hostKeys = Object.keys(catalogData.datasets).filter(hostKey => 
                                catalogData.datasets[hostKey][datasetKey]);
                            return hostKeys.every(hostKey => {
                                const combinedKey = `${datasetKey}-:-${hostKey}`;
                                return selectedDatasets[combinedKey] === true;
                            });
                          })}
                          onChange={handleSelectFilteredDatasets}>
                          <Text fontSize={theme.fontSize.info}>
                            Select all
                          </Text>
                        </Checkbox>
                        <Divider mb={1}/>
                        {filteredDatasets.map((datasetKey) => {
                          const hostKeys = Object.keys(catalogData.datasets).filter(hostKey => 
                              catalogData.datasets[hostKey][datasetKey]);
                        
                          return hostKeys.map(hostKey => {
                            const shortenedHostKey = hostKeyAbbrevations[hostKey] || hostKey;
                            return(
                              <Box key={`${datasetKey}-:-${hostKey}`} display="flex" alignItems="center">
                                <Checkbox 
                                    isChecked={selectedDatasets[`${datasetKey}-:-${hostKey}`]}
                                    onChange={(e) => handleDatasetsChange(datasetKey, hostKey, e.target.checked)}>
                                    <Text fontSize={theme.fontSize.info}>
                                        {datasetKey}
                                    </Text>
                                </Checkbox>
                                <Tooltip
                                  label={hostKey} aria-label={`${hostKey} full name`}
                                  placement="top" hasArrow bg="Black" >
                                  <Badge colorScheme="green" ml={1} fontSize="0.5rem">
                                    {shortenedHostKey}
                                  </Badge>
                                </Tooltip>
                              </Box>
                            )}
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                  <Box className="discreet-scrollbar" maxHeight="450px" overflowY="auto">
                    <Table
                      size="xs" colorScheme="blackAlpha">
                      <Thead>
                        <Tr>
                          <Th color="white" borderBottom="1px solid"
                            borderColor="white" mb={2} pb={2}
                            width="1%" whiteSpace="nowrap"
                            fontSize={theme.fontSize.info}>
                          </Th>
                          <Th 
                            width="52%"
                            color="white" borderBottom="1px solid" 
                            borderColor="white" pb={1}
                            fontSize={theme.fontSize.info}>
                            Dataset
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                       {Object.entries(catalogData.datasets).map(([hostKey, datasetsItem]) => {
                         return Object.entries(datasetsItem).sort(([datasetKeyA], [datasetKeyB]) => datasetKeyA.localeCompare(datasetKeyB))
                         .map(([datasetKey, datasetDetails]) => (
                           <Tr key={`${datasetKey}-:-${hostKey}`}>
                             <Td fontSize={theme.fontSize.info}>
                               <Checkbox
                                 isChecked={selectedDatasets[`${datasetKey}-:-${hostKey}`]}
                                 onChange={(e) => handleDatasetsChange(datasetKey, hostKey, e.target.checked)}
                               />
                             </Td>
                             <Td>
                             <Card
                                id={`dataset-card-${datasetKey}-:-${hostKey}`}
                                width="100%" mt={2}
                                bg={theme.colors.main.results}
                                borderRadius={theme.radii.card}>
                                <CardHeader px={2} py={2}>
                                <Heading
                                  color="black"
                                  fontSize={theme.fontSize.cardName}>
                                  {datasetKey}
                                </Heading>
                                <Badge
                                  colorScheme='green'>
                                  {hostKey}
                                </Badge>
                                </CardHeader>
                                <Divider borderColor={"black"}/>
                                <CardBody px={2} py={2}>
                                  <Text 
                                    color="black" fontSize={theme.fontSize.info}>
                                    {datasetDetails.description 
                                    ? Array.isArray(datasetDetails.description) 
                                      ? datasetDetails.description.join(' - ') 
                                      : datasetDetails.description 
                                    : 'Description not available'}
                                  </Text>
                                </CardBody>
                              </Card>
                             </Td>
                           </Tr>
                         ));
                       })}
                     </Tbody>
                    </Table>
                  </Box>
                  {errorDataset &&
                    <Text
                      fontSize={theme.fontSize.info}
                      mt={2} color={theme.colors.main.warning}>
                      {errorDataset}
                    </Text>
                  }
                </FormControl>
                <Divider mt={6} mb={4}/>
                <FormControl>
                  <FormLabel
                    mb={2} mt={2}
                    fontSize={theme.fontSize.cardName}>
                    04 Cloud Coverage
                  </FormLabel>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Specify your maximum acceptable cloud coverage rate in percent.
                    The default value is 100 %.
                  </Text>
                  <InputGroup>
                    <NumberInput
                      width="100%" min={0} keepWithinRange={true}
                      value={cloudsInput}
                      step={0.1}
                      onChange={handleCloudsChange}>
                      <NumberInputField
                      placeholder="100"
                      borderTopRightRadius="none"
                      borderBottomRightRadius="none"/>
                    </NumberInput>
                    <InputRightAddon width="auto" color="black">
                      %
                    </InputRightAddon>
                  </InputGroup>
                </FormControl>
                <Divider mt={6} mb={4}/>
                <FormControl>
                  <FormLabel
                    mb={2} mt={2}
                    fontSize={theme.fontSize.cardName}>
                    05 AoI Coverage
                  </FormLabel>
                  <Text
                    mb={2}
                    fontSize={theme.fontSize.info}>
                    Enable to only get images that fully cover the
                    defined area of interest.
                  </Text>
                  <HStack>
                    <Switch
                      isChecked={fullAoiInput}
                      onChange={handleFullAoiChange} />
                    <Text fontSize={theme.fontSize.body}>
                      100 % AoI Coverage
                    </Text>
                  </HStack>
                </FormControl>
                <VStack align="left">
                  <Button
                    mt={8} onClick={handleSubmit}
                    isLoading={isSubmittingRequest}
                    loadingText="Processing">
                    Submit
                  </Button>
                </VStack>
              </TabPanel>

              {/* Result Tab */}
              <TabPanel>
                <Text 
                  mb={2} 
                  fontFamily="label" fontSize={theme.fontSize.body}>
                  Archive Data
                </Text>
                <HStack>
                  <Circle
                    sx={{ color: 'black' }} 
                    fontSize={theme.fontSize.iconSingle}/>
                  <Text fontSize={theme.fontSize.body}>
                    Area of Interest
                  </Text>
                </HStack>

                {/* Request not send yet */}
                {responseSuccess === false && (
                  <Card
                    display="flex"
                    borderRadius={theme.radii.card}
                    justifyContent="center" alignItems="center"
                    mt={2} width="100%"
                    bg={theme.colors.main.results}
                    border="1px">
                    <CardBody>
                      {isSubmittingRequest === false && (
                      <Text
                        color="black" fontSize={theme.fontSize.info}>
                        No Results. Send Request.
                      </Text>
                      )}
                      {isSubmittingRequest && <Spinner color='black' />} 
                    </CardBody>
                  </Card>
                )}

                {/* Request failed */}
                {errorRequest &&
                  <Text
                    mt={2}
                    fontSize={theme.fontSize.info}
                    color={theme.colors.main.warning}>
                    {errorRequest}
                  </Text>
                }

                {/* Request success */}
                {responseSuccess && responseData.data && (
                  <Box>
                    {/* General Stats */}
                    <Divider my={4} />
                    <Stat>
                      <StatLabel
                        fontFamily={"label"}
                        color={theme.colors.main.results}
                        fontSize={theme.fontSize.body}>
                        Total Items
                      </StatLabel>
                      <StatNumber>{responseData.n_data}</StatNumber>
                      <StatHelpText
                        fontSize={theme.fontSize.info}>
                        {formatUTCShort(startDateInput)} - {formatUTCShort(endDateInput)}
                      </StatHelpText>
                    </Stat>
                    {/* Results */}
                    {Object.keys(responseData.data).length > 0 && (
                      <Box>
                        {/* List of items */}
                        <Divider my={4} />
                        <Text
                          mb={1}
                          variant={"results"} fontFamily={"label"}
                          fontSize={theme.fontSize.body}>
                          List
                        </Text>
                        <Box
                          className="discreet-scrollbar"
                          maxHeight={`375px`}
                          overflowY="auto">
                          {Object.entries(responseData.data).flatMap(([hostKey, resultItemCollection]) => {
                            // Display specifically for "planetary_computer"
                            if (hostKey === "planetary_computer") {
                              return resultItemCollection.features?.map((resultItemPC, itemIndexPC) => (
                                <Card 
                                  id={`result-card-${hostKey}-${itemIndexPC}`}
                                  key={`${hostKey}-${itemIndexPC}`}
                                  bg={selectedResultId === `${hostKey}-${itemIndexPC}` ? theme.colors.main.selected : theme.colors.main.results}
                                  borderRadius={theme.radii.card}
                                  cursor={"pointer"}
                                  _hover={{bg: theme.colors.main.selected}}
                                  onClick={() => {
                                    setSelectedResultId(`${hostKey}-${itemIndexPC}`);
                                    setSelectedIndex(itemIndexPC);}}
                                  width="100%" mb={2}>
                                  <CardBody px={2} py={2}>
                                    <Flex>
                                      <VStack align="start" spacing={1} flex="1">
                                        <Heading
                                          color="black"
                                          fontSize={theme.fontSize.cardName}>
                                          {resultItemPC.properties.platform}
                                        </Heading>
                                        <HStack whiteSpace="nowrap">
                                          <Text
                                            color="black"
                                            fontSize={theme.fontSize.info}>
                                            {formatUTCAlternative(resultItemPC.properties.datetime)}
                                          </Text>
                                        </HStack>
                                        <HStack mt={1} whiteSpace="nowrap">
                                          <Badge colorScheme='green'>{hostKey}</Badge>
                                        </HStack>
                                      </VStack>
                                      <Spacer/>
                                      <Tooltip
                                        label="Show on Map" aria-label='Map Display Tooltip'
                                        placement='top' hasArrow bg="Black" >
                                        <Box
                                          maxW="70px"
                                          width="100%"
                                          cursor="pointer"
                                          onClick={() => handleResultDisplay(
                                            resultItemPC.assets?.tilejson?.href,
                                            `${hostKey}-${itemIndexPC}`,
                                            itemIndexPC
                                          )}
                                          >
                                          <Image
                                            src={resultItemPC.assets?.rendered_preview?.href || placeholderImage}
                                            alt="Preview"
                                            borderRadius={theme.radii.box}
                                            height="auto"
                                            width="100%"
                                          />
                                        </Box>
                                      </Tooltip>
                                    </Flex>
                                  </CardBody>
                                </Card>
                              )) || []
                            }
                          
                            //Return an empty array if hostKey is unknown
                            return [];
                          })}
                        </Box>
                        {/* Details*/}
                        <Text 
                          mt={4} mb={1} variant={"results"}
                          fontFamily="label" fontSize={theme.fontSize.body}>
                          Details
                        </Text>
                        {/* Detail Cards for Planetary Computer */}
                        {(selectedResultId && selectedResultId.startsWith('planetary_computer-') &&
                         selectedIndex !== null && responseData.data["planetary_computer"].features) ? (
                          (() => {
                            const pcItem = responseData.data["planetary_computer"].features[selectedIndex];
                            return(
                              <Box>
                                <Image
                                  src={pcItem.assets?.rendered_preview?.href || placeholderImage}
                                  alt="Preview"
                                  borderRadius={theme.radii.box}
                                  height="auto"  mt={1}
                                  width="100%"/>
                                <Grid templateColumns="auto 1fr" gap={2} mt={2}>
                                  <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                    Platform:
                                  </Text>
                                  <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak="break-all" display="inline">
                                    {pcItem.properties.platform}
                                  </Text>
                                  <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                    Datetime:
                                  </Text>
                                  <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak="break-all" display="inline">
                                    {formatUTC(pcItem.properties.datetime)}
                                  </Text>
                                  <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                    Clouds:
                                  </Text>
                                  <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak="break-all" display="inline">
                                    {pcItem.properties["eo:cloud_cover"] !== undefined ? pcItem.properties["eo:cloud_cover"] : "no data"} %
                                  </Text>
                                  <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                    ID:
                                  </Text>
                                  <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak="break-all" display="inline">
                                    {pcItem.id}
                                  </Text>
                                  <Text variant={"results"} fontSize={theme.fontSize.info} whiteSpace="nowrap">
                                    bbox:
                                  </Text>
                                  <Text variant={"results"} fontFamily="monoThin" fontSize={theme.fontSize.info} wordBreak='break-all' display="inline">
                                    {pcItem.bbox.join(' ')}
                                  </Text>
                                </Grid>
                              </Box>
                            );
                          })()
                          ) : (
                          <Card
                            display="flex"
                            borderRadius={theme.radii.card}
                            justifyContent="center" alignItems="center"
                            mt={2} width="100%"
                            bg={theme.colors.main.results}>
                            <CardBody>
                              <Text color="black" fontSize={theme.fontSize.info}>
                                Select or Display Item.
                              </Text>
                            </CardBody>
                          </Card>
                        )}
                      </Box>
                    )}
                  </Box>
                )}      
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Grid>  
    </Flex>
  );
};

export default BodyEOx;